import {
  EllipsisSpan,
  Paging,
  Table,
  useBreadcrumbRoutes,
  usePaging,
  Wrapper,
  Button,
  Form,
  Select,
  CustomFilter,
  useUpdate,
  message,
  PopConfirm,
  MultiSelect,
} from '@maxtropy/components';
import { Space } from 'antd';
import { ColumnType } from 'antd/es/table';
import dayjs from 'dayjs';
import { FC, useEffect, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import CollocatedModal from './CollocatedModal';
import {
  apiV2EnergyConsumptionPlanDeleteOrgMappingPost,
  apiV2EnergyConsumptionPlanGetStaffDeptPost,
  apiV2EnergyConsumptionPlanGetAreaListPost,
  apiV2EnergyConsumptionPlanQueryWhetherCanBeModifiedPost,
  V2EnergyConsumptionPlanGetAreaListPostResponse,
  V2EnergyConsumptionPlanGetStaffDeptPostResponse,
  V2EnergyConsumptionPlanGetOrgMappingPagePostResponse,
  V2EnergyConsumptionPlanGetOrgMappingPagePostRequest,
  apiV2EnergyConsumptionPlanGetOrgMappingPagePost,
} from '@maxtropy/device-customer-apis-v2';
import ConfigModal from './components/configModal';
export type OrgMapItem = Exclude<V2EnergyConsumptionPlanGetOrgMappingPagePostResponse['list'], undefined>[number];
const columns: ColumnType<OrgMapItem>[] = [
  {
    title: '部门',
    dataIndex: 'ownerOrgName',
    width: 200,
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '申请人组织',
    dataIndex: 'orgList',
    width: 200,
    ellipsis: { showTitle: true },
    render: v => <EllipsisSpan value={v.map((i: any) => i.orgName).join(',')} />,
  },
  {
    title: '区域',
    dataIndex: 'areaList',
    width: 200,
    ellipsis: { showTitle: true },
    render: v => <EllipsisSpan value={v.map((i: any) => i.zoneName).join(',')} />,
  },
  {
    title: '最后操作人',
    dataIndex: 'updateUser',
    width: 200,
    ellipsis: { showTitle: true },
    render: (value: string, record) => {
      return <EllipsisSpan value={value} />;
    },
  },
  {
    title: '最后编辑时间',
    dataIndex: 'updateTime',
    width: 200,
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={dayjs(v).format('YYYY-MM-DD HH:mm:ss')} />,
  },
];

export type OrgMapProps = Exclude<V2EnergyConsumptionPlanGetOrgMappingPagePostResponse['list'], undefined>[number];

export type SearchProps = V2EnergyConsumptionPlanGetOrgMappingPagePostRequest;
const text = '你确定要删除吗？';
const OrgMappingManage: FC = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [loading, setLoading] = useState<boolean>(false);
  const pagingInfo = usePaging(10);
  const [form] = Form.useForm();
  const [update, setUpdate] = useUpdate();
  const [sectionOp, setSectionOp] = useState<V2EnergyConsumptionPlanGetStaffDeptPostResponse['list']>([]);
  const [areaOp, setAreaOp] = useState<V2EnergyConsumptionPlanGetAreaListPostResponse['list']>([]);
  const [searchParams, setSearchParams] = useState<SearchProps>();
  const [collocateModal, setCollocateModal] = useState<boolean>(false);
  const [data, setData] = useState<OrgMapProps[]>([]);
  const [editNodeData, setEditNodeData] = useState<OrgMapProps | undefined>();
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [showConfigModal, setShowConfigModal] = useState<boolean>(false);
  const orgId = Form.useWatch('ownerOrgId', form);

  useEffect(() => {
    setLoading(true);
    apiV2EnergyConsumptionPlanGetOrgMappingPagePost({ ...searchParams, page: pageOffset, size: pageSize })
      .then(res => {
        setData(res.list ?? []);
        setLoading(false);
        setTotalCount(res.total ?? 0);
      })
      .finally(() => setLoading(false));
  }, [pageOffset, pageSize, searchParams, setTotalCount, update]);

  useEffect(() => {
    apiV2EnergyConsumptionPlanGetStaffDeptPost({}).then(res => {
      setSectionOp(res.list);
    });
  }, []);

  useEffect(() => {
    if (!orgId) return;
    apiV2EnergyConsumptionPlanGetAreaListPost({ id: orgId }).then(res => {
      setAreaOp(res.list);
    });
  }, [orgId]);

  const deleteRecord = async (val: number) => {
    const canDelete = await onChangeConfirm(val);
    if (!canDelete) return;
    apiV2EnergyConsumptionPlanDeleteOrgMappingPost({ id: val }).then(res => {
      setUpdate();
    });
  };

  const onFinish = (val: SearchProps) => {
    setPageOffset(1);
    setSearchParams(val);
  };

  const onReset = () => {
    setPageOffset(1);
    setAreaOp([]);
    setSearchParams({});
  };

  const onChangeConfirm = async (id: number): Promise<boolean> => {
    const res = await apiV2EnergyConsumptionPlanQueryWhetherCanBeModifiedPost({ id });
    if (res.editable) {
      setIsEdit(false);
      return true;
    } else {
      message.warning('正在审批中，无法修改或删除');
      setIsEdit(true);
      return false;
    }
  };

  const filters = (
    <CustomFilter<OrgMapItem> form={form} onFinish={val => onFinish(val as SearchProps)} onReset={onReset}>
      <Form.Item name="ownerOrgId" label="部门">
        <Select
          style={{ width: '100%' }}
          placeholder="请选择"
          options={sectionOp?.map(s => ({ label: s.ownerOrgName, value: s.ownerOrgId }))}
          showSearch
          optionFilterProp="label"
          onChange={() => {
            form.setFieldValue('areaIdList', undefined);
          }}
        />
      </Form.Item>
      <Form.Item name="areaIdList" label="区域">
        <MultiSelect
          placeholder="请选择"
          options={areaOp?.map(a => ({ label: a.zoneName, value: a.id }))}
          showSearch
          optionFilterProp="label"
        />
      </Form.Item>
    </CustomFilter>
  );

  const buildColumns = [
    ...columns,
    {
      title: '操作',
      dataIndex: 'operation',
      width: 160,
      fixed: 'right' as const,
      render: (value: undefined, record: OrgMapItem) => {
        return (
          <Space size={16}>
            <Button
              type="link"
              disabled={!record.admin}
              onClick={async () => {
                await onChangeConfirm(record.id!);
                setEditNodeData({ id: record.id });
                setCollocateModal(true);
              }}
            >
              编辑
            </Button>
            <PopConfirm placement="top" title={text} onConfirm={() => deleteRecord(record.id!)}>
              <Button disabled={!record.admin} type="link">
                删除
              </Button>
            </PopConfirm>
            <Button
              type="link"
              disabled={!record.admin}
              onClick={() => {
                setShowConfigModal(true);
                setEditNodeData({ id: record.id, limitTime: record.limitTime });
              }}
            >
              计划申请配置
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <Wrapper routes={breadcrumbRoutes?.routes} className="page_wrapper" filters={filters}>
      <Button
        icon={<PlusOutlined />}
        wrapStyle={{ marginBottom: 10 }}
        type="primary"
        onClick={() => {
          setEditNodeData(undefined);
          setCollocateModal(true);
          setIsEdit(false);
        }}
      >
        新增配置
      </Button>
      <Table rowKey="id" sticky loading={loading} dataSource={data} columns={buildColumns} />
      <Paging pagingInfo={pagingInfo} />
      {collocateModal && (
        <CollocatedModal
          visible={collocateModal}
          isEdit={isEdit}
          room={editNodeData?.id}
          updateFn={setUpdate}
          onClose={() => setCollocateModal(false)}
        />
      )}
      {showConfigModal && (
        <ConfigModal updateFn={setUpdate} data={editNodeData} onClose={() => setShowConfigModal(false)} />
      )}
    </Wrapper>
  );
};

export default OrgMappingManage;
